import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useMediaQuery, useTheme } from "@mui/material";
import { useQuery, useQueryClient } from "react-query";
import { WORKSPACE_RAIL_COUNTS_KEY, invalidateWorkspaceRailCounts } from "../../../hooks/useWorkspaceRailCounts";

import { useAuthContext } from "../../../context/AuthContext";
import { useNotificationsOptional } from "../../../context/NotificationContext";
import { useConversations } from "../../../hooks/useConversations";
import { useConversationDetails } from "../../../hooks/useConversationDetails";
import { useMessages } from "../../../hooks/useMessages";
import { useRealtimeMessages } from "../../../hooks/useRealtimeMessages";
import {
  getConversationMessagesById,
  deleteConversationMessage,
  reactConversationMessage,
  editConversationWhatsappMessage,
  forwardConversationWhatsappMessage,
  editConversationNote,
  deleteConversationNote,
  fetchData,
  getPersonalWhatsappChatsList,
  getPersonalWhatsappChatMessages,
  getPersonalChatKeyFromSyntheticConversationId,
  deletePersonalWhatsappChat,
} from "../../../api/client";
import { useCreateConversation } from "../../../hooks/useCreateConversation"; 
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import useRecordLeadAccess from "./useRecordLeadAccess";
import { getConversationInsights } from "../../../api/client";
import { enrichMessageLocationFromApi } from "../../../utils/enrichMessageLocation";
import { deliveryStatusFromPersonalWhatsappStatus } from "../../../components/MessageHistory/messageDeliveryStatus";
import { toE164Digits, sanitizeStoredPhoneForDisplay } from "../../../utils/phone";
import {
  parseLeadAccessDeniedPayload,
  mergeDeniedContext,
  isLeadAccessDeniedStatus,
} from "../../../utils/conversationAccessDenied";
import {
  readWorkspaceLocal,
  readWorkspaceLocalJson,
  writeWorkspaceLocal,
  writeWorkspaceLocalJson,
  WS_KEY,
  getActiveWorkspaceSlug,
} from "../../../utils/workspaceStorage";
import { workspaceScopedQueryKey } from "../../../utils/workspaceQueryScope";
import {
  isWorkspaceNavSearch,
  isPopupCallPath,
  isPopupServiceChatPath,
  isServiceChatPath,
  resolveServiceChatNavPath,
} from "../../../utils/teamNavigation";
import { getTeamPaneContainerStyle } from "../../Team/utils/teamUiUtils";
import {
  EMPTY_SEARCH_FILTER_STATE,
  filterStateToAdvancedParams,
  normalizeSearchFilterState,
} from "../../../utils/searchFilterState";
import {
  resolveChatListApiSearchTerm,
  stripClientOnlyChatFilters,
} from "../utils/chatListContext";
import { hasUnreadClientMessage } from "../../../utils/chatUnreadUtils";

// ✅ Função auxiliar para padronizar o status
const getStatusLabelFromCode = (status) => {
  const statusMap = {
    0: "Pendente",
    1: "Em Atendimento",
    2: "Concluída",
    3: "Contato Futuro",
    4: "Perdida",
    5: "Cancelada",
  };

  if (typeof status === "string" && !isNaN(Number(status))) {
    status = Number(status);
  }

  return statusMap[status] ?? "Desconhecido";
};

function mapPersonalRedisMessageToUi(m, userData) {
  if (!m) return null;
  const uid = userData?.id;
  const isOut = m.direction === "out";
  const ts = m.ts != null ? Number(m.ts) : Date.now();
  const iso = new Date(ts).toISOString();
  const arquivoUrl = m.arquivo || null;
  const row = {
    id: m.id,
    text: m.text || "",
    mensagem: m.text || "",
    tipo: 3,
    sender: isOut ? "operator" : "client",
    senderType: isOut ? "agent" : "client",
    id_usuario: isOut ? uid : 0,
    senderId: isOut ? uid : undefined,
    timestamp: iso,
    createdAt: iso,
    lastUpdate: iso,
    data_cadastro: iso.replace("T", " ").slice(0, 19),
    ts,
    imagem: m.imagem || null,
    image: m.imagem || null,
    video: m.video || null,
    audio: m.audio || null,
    arquivo: arquivoUrl,
  };
  if (arquivoUrl) {
    try {
      const u = new URL(String(arquivoUrl));
      const base = u.pathname.split("/").pop() || "documento";
      row.document = { url: arquivoUrl, name: decodeURIComponent(base) };
    } catch {
      row.document = { url: arquivoUrl, name: "documento" };
    }
  }
  if (m.imagem) row.type = "image";
  else if (m.audio) row.type = "audio";
  else if (m.video) row.type = "video";
  else if (m.arquivo) row.type = "document";
  else row.type = "text";
  if (isOut) {
    const nm = String(userData?.nome || userData?.name || "").trim();
    if (nm) row.senderName = nm;
    row.deliveryStatus = deliveryStatusFromPersonalWhatsappStatus(m.statusWhatsapp);
  }
  if (m.waMessageId != null && String(m.waMessageId).trim()) {
    row.waMessageId = String(m.waMessageId).trim();
  }
  return enrichMessageLocationFromApi(row);
}

/**
 * Otimista (temp- / status sending) já coberta por linha vinda do Redis (mesmo waMessageId ou mesmo texto outbound recente).
 */
function personalPendingSupersededByServerList(serverUiMessages, pendingKey, pendingMsg, userData) {
  const isPending =
    String(pendingKey).startsWith("temp-") ||
    pendingMsg?.status === "sending" ||
    pendingMsg?.status === "error";
  if (!isPending) return false;
  const wa = String(pendingMsg?.waMessageId || pendingMsg?.wa_message_id || "").trim();
  if (wa && serverUiMessages.some((s) => String(s?.waMessageId || s?.wa_message_id || "").trim() === wa)) {
    return true;
  }
  const txt = String(pendingMsg?.text || "").trim();
  if (!txt) return false;
  const uid = userData?.id;
  const isAgentOut =
    pendingMsg?.senderType === "agent" ||
    pendingMsg?.sender === "operator" ||
    (uid != null && Number(pendingMsg?.id_usuario) === Number(uid));
  if (!isAgentOut) return false;
  const msgTs =
    Number(pendingMsg?.ts) ||
    new Date(pendingMsg?.timestamp || pendingMsg?.createdAt || pendingMsg?.lastUpdate || 0).getTime() ||
    0;
  return serverUiMessages.some((s) => {
    const outbound =
      s?.senderType === "agent" ||
      s?.sender === "operator" ||
      (uid != null && Number(s?.id_usuario) === Number(uid));
    if (!outbound) return false;
    if (String(s?.text || "").trim() !== txt) return false;
    const sTs =
      Number(s?.ts) ||
      new Date(s?.timestamp || s?.createdAt || s?.lastUpdate || 0).getTime() ||
      0;
    // Eco do Redis costuma chegar poucos s após o otimista; evita colidir com outro envio igual mais tarde.
    return sTs >= msgTs - 5000 && sTs <= msgTs + 120_000;
  });
}

function stripHtmlSimple(s) {
  if (!s || typeof s !== "string") return "";
  return s.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

/**
 * Remove mensagem otimista temp-* quando o servidor já devolveu o mesmo envio (socket/polling).
 * Evita duplicação visual enquanto status === "sending" (comum em anotações internas).
 */
function regularConversationPendingSupersededByServer(serverMessages, pendingKey, pendingMsg, userData) {
  const isPending =
    String(pendingKey).startsWith("temp-") ||
    pendingMsg?.status === "sending" ||
    pendingMsg?.status === "error";
  if (!isPending) return false;

  const txt = stripHtmlSimple(String(pendingMsg?.text || pendingMsg?.mensagem || "").trim());
  if (!txt) return false;

  const uid = userData?.id;
  const isAgentOut =
    pendingMsg?.senderType === "agent" ||
    pendingMsg?.sender === "operator" ||
    pendingMsg?.sender === "Agent" ||
    (uid != null && Number(pendingMsg?.senderId) === Number(uid));

  if (!isAgentOut) return false;

  const msgTs =
    Number(pendingMsg?.ts) ||
    new Date(pendingMsg?.timestamp || pendingMsg?.createdAt || pendingMsg?.lastUpdate || 0).getTime() ||
    0;

  return serverMessages.some((s) => {
    const sTxt = stripHtmlSimple(String(s?.text || s?.mensagem || "").trim());
    if (sTxt !== txt) return false;

    const outbound =
      s?.senderType === "agent" ||
      s?.sender === "operator" ||
      s?.sender === "Agent" ||
      (uid != null && Number(s?.id_usuario) === Number(uid));

    if (!outbound) return false;

    const sTs =
      Number(s?.ts) ||
      new Date(s?.timestamp || s?.createdAt || s?.lastUpdate || s?.updatedAt || 0).getTime() ||
      0;

    if (!msgTs || !sTs) return true;

    return sTs >= msgTs - 5000 && sTs <= msgTs + 120_000;
  });
}

/** Foco do atendente ao logar: minhas solicitações pendentes e em atendimento ("Meu dia") */
const DEFAULT_FILTERS = [
  "Minhas Solicitações",
  "Pendente",
  "Em Atendimento",
];

function loadPersistedFilters() {
  try {
    const parsed = readWorkspaceLocalJson(WS_KEY.CHAT_FILTERS, null);
    if (!Array.isArray(parsed)) return DEFAULT_FILTERS;
    const valid = parsed.filter((f) => typeof f === "string");
    return valid.length ? valid : DEFAULT_FILTERS;
  } catch {
    return DEFAULT_FILTERS;
  }
}

function loadPersistedAdvancedFilters() {
  try {
    const parsed = readWorkspaceLocalJson(WS_KEY.CHAT_ADVANCED_FILTERS, null);
    if (!parsed || typeof parsed !== "object") return EMPTY_SEARCH_FILTER_STATE;
    return normalizeSearchFilterState(parsed);
  } catch {
    return EMPTY_SEARCH_FILTER_STATE;
  }
}

function loadPersistedSearch() {
  try {
    const s = readWorkspaceLocal(WS_KEY.CHAT_SEARCH);
    return typeof s === "string" ? s : "";
  } catch {
    return "";
  }
}

function loadLastSeenClientAt() {
  try {
    const parsed = readWorkspaceLocalJson(WS_KEY.CHAT_LAST_SEEN, {});
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

/** Corpo lançado por `fetchData` em erro: costuma preservar `code` e `data` do backend. */
function getApiErrorMeta(err) {
  const code =
    (typeof err?.code === "string" && err.code) ||
    (typeof err?.data?.code === "string" && err.data.code) ||
    null;
  const duplicatesFromData = err?.data?.duplicates;
  const duplicates =
    Array.isArray(duplicatesFromData)
      ? duplicatesFromData
      : Array.isArray(err?.duplicates)
        ? err.duplicates
        : [];
  const message =
    (typeof err?.message === "string" && err.message) ||
    (typeof err?.data?.message === "string" && err.data.message) ||
    null;
  return { code, duplicates, message };
}

export default function useChatState() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const popupCallMode = isPopupCallPath(location.pathname);
  const navigateChat = useCallback(
    (path) =>
      navigate(
        resolveServiceChatNavPath(path, {
          pathname: location.pathname,
          search: location.search,
        })
      ),
    [navigate, location.pathname, location.search]
  );
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const { userData } = useAuthContext();
  const notificationsCtx = useNotificationsOptional();
  const queryClient = useQueryClient();

  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messagesMap, setMessagesMap] = useState(new Map());
  const selectedConversationIdRef = useRef(selectedConversationId);
  selectedConversationIdRef.current = selectedConversationId;

  const isActiveConversationId = useCallback((conversationId) => {
    if (conversationId == null || selectedConversationIdRef.current == null) return false;
    return String(selectedConversationIdRef.current) === String(conversationId);
  }, []);

  useRecordLeadAccess({
    selectedConversationId,
    routeLeadId: id,
    origem: popupCallMode ? "popup" : "chat",
  });

  /** Só aplica updates no mapa se a solicitação ainda for a ativa (evita mistura A→B). */
  const applyMessagesIfActive = useCallback((conversationId, updater) => {
    if (!isActiveConversationId(conversationId)) return false;
    setMessagesMap((prev) => {
      // Re-check dentro do updater: id pode ter mudado entre o check e o flush do React.
      if (!isActiveConversationId(conversationId)) return prev;
      return updater(prev);
    });
    return true;
  }, [isActiveConversationId]);

  const tagMessageConversationId = useCallback((msg, conversationId) => {
    if (!msg || conversationId == null) return msg;
    return { ...msg, conversationId: String(conversationId) };
  }, []);

  const messages = useMemo(() => {
    const activeId =
      selectedConversationId != null ? String(selectedConversationId) : null;
    if (!activeId) return [];
    return Array.from(messagesMap.values()).filter(
      (m) => String(m?.conversationId) === activeId
    );
  }, [messagesMap, selectedConversationId]);

  const onSocketActivity = useCallback(
    (evt) => {
      const isConversationSelected =
        selectedConversationId != null &&
        String(selectedConversationId) === String(evt.conversationId);
      notificationsCtx?.dispatchRealtimeSocket?.({
        ...evt,
        isConversationSelected,
      });
    },
    [notificationsCtx, selectedConversationId]
  );
  const { sendMessageMutation } = useMessages();

  const { createConversation, isCreating, errorCreating } = useCreateConversation();

  const [aiInsights, setAiInsights] = useState(null);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [aiInsightsError, setAiInsightsError] = useState(null);
  const [aiInsightsConversationId, setAiInsightsConversationId] = useState(null);

  const [initialLoading, setInitialLoading] = useState(true);
  const [showConversationList, setShowConversationList] = useState(() => !popupCallMode);
  const [showMessageHistory, setShowMessageHistory] = useState(() =>
    popupCallMode ? true : !isMobile
  );
  const [showConversationDetails, setShowConversationDetails] = useState(() =>
    popupCallMode ? true : false
  );
  const [searchTerm, setSearchTerm] = useState(loadPersistedSearch);
  const [filters, setFilters] = useState(loadPersistedFilters);
  const [advancedFilterState, setAdvancedFilterState] = useState(loadPersistedAdvancedFilters);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [requestTotal, setRequestTotal] = useState(false);
  const hasRequestedTotal = useRef(false);
  const [lastSeenClientAtMap, setLastSeenClientAtMap] = useState(loadLastSeenClientAt);
  const [messagesPageState, setMessagesPageState] = useState({
    hasMore: false,
    lastMessageId: null,
    isInitialLoaded: false,
    isLoadingMore: false,
  });
  /** Falha ao carregar/sincronizar histórico (ex.: duplicidade de cliente no backend). */
  const [messagesSyncError, setMessagesSyncError] = useState(null);
  /** 403 do GET de mensagens: mesmo gate de detalhes (tipo/empresa/visibilidade). */
  const [messagesAccessDenied, setMessagesAccessDenied] = useState(null);

  // Persistir filtros e busca para manter após reload
  useEffect(() => {
    try {
      writeWorkspaceLocalJson(WS_KEY.CHAT_FILTERS, filters);
    } catch (e) {
      console.warn("Falha ao salvar filtros do chat no localStorage", e);
    }
    invalidateWorkspaceRailCounts(queryClient);
  }, [filters, queryClient]);

  useEffect(() => {
    try {
      writeWorkspaceLocal(WS_KEY.CHAT_SEARCH, searchTerm);
    } catch (e) {
      console.warn("Falha ao salvar busca do chat no localStorage", e);
    }
    invalidateWorkspaceRailCounts(queryClient);
  }, [searchTerm, queryClient]);

  useEffect(() => {
    try {
      writeWorkspaceLocalJson(WS_KEY.CHAT_ADVANCED_FILTERS, advancedFilterState);
    } catch (e) {
      console.warn("Falha ao salvar filtros avançados do chat no localStorage", e);
    }
    invalidateWorkspaceRailCounts(queryClient);
  }, [advancedFilterState, queryClient]);

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500);
  const apiSearchTerm = useMemo(
    () => resolveChatListApiSearchTerm(debouncedSearchTerm),
    [debouncedSearchTerm]
  );
  const debouncedFilters = useDebouncedValue(filters, 400);
  const debouncedAdvancedFilterState = useDebouncedValue(advancedFilterState, 400);
  // Pipeline afeta tipo_scope na API: usar estado atual do chip, não o valor debounced,
  // para não pedir relacionamento (t.tipo=1) por ~400ms após ligar o filtro.
  const isSalesPipelineSelected = (filters || []).includes("Pipeline de Vendas");
  const debouncedFiltersForApi = stripClientOnlyChatFilters(debouncedFilters);

  const personalFilterOn = (filters || []).includes("WhatsApp pessoal");

  const workspaceSlug = getActiveWorkspaceSlug();
  const prevWorkspaceSlugRef = useRef(workspaceSlug);

  const {
    data: conversationDetails,
    isLoading: isLoadingDetails,
    isError: isDetailsError,
    error: detailsError,
    refetch: refetchConversationDetails,
  } = useConversationDetails(selectedConversationId);
  const detailsQueryDenied =
    isDetailsError && isLeadAccessDeniedStatus(detailsError);
  const detailsAccessDenied = detailsQueryDenied || Boolean(messagesAccessDenied);
  const detailsDenied = detailsAccessDenied
    ? mergeDeniedContext(
        detailsQueryDenied ? parseLeadAccessDeniedPayload(detailsError) : null,
        messagesAccessDenied
      )
    : null;

  const applyMessagesFetchError = useCallback((err, requestId, options = {}) => {
    const personal = getPersonalChatKeyFromSyntheticConversationId(requestId);
    if (!personal && isLeadAccessDeniedStatus(err)) {
      setMessagesAccessDenied(parseLeadAccessDeniedPayload(err));
      setMessagesSyncError(null);
      setIsLoadingConversation(false);
      return true;
    }
    const meta = getApiErrorMeta(err);
    if (options.duplicatesOnly) {
      if (meta.code === "errors.clients.duplicate" && meta.duplicates.length) {
        setMessagesSyncError({
          code: meta.code,
          message: meta.message || options.fallbackMessage,
          duplicates: meta.duplicates,
        });
      }
      return false;
    }
    if (options.mergeSyncError) {
      setMessagesSyncError((prev) => {
        if (meta.code === "errors.clients.duplicate" && meta.duplicates.length) {
          return {
            code: meta.code,
            message: meta.message || prev?.message || options.fallbackMessage,
            duplicates: meta.duplicates,
          };
        }
        if (prev?.code === "errors.clients.duplicate") return prev;
        return {
          code: meta.code,
          message: meta.message || options.fallbackMessage,
          duplicates: [],
        };
      });
      return false;
    }
    setMessagesSyncError({
      code: meta.code,
      message: meta.message || options.fallbackMessage,
      duplicates: meta.duplicates || [],
    });
    return false;
  }, []);

  const {
    data,
    isLoading,
    isError,
    error: conversationsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    refetch,
  } = useConversations(
    apiSearchTerm,
    debouncedFiltersForApi,
    (() => {
      const quemFilters = [
        "Minhas Solicitações",
        "Outros Usuários",
        "Fila",
        "ChatBots",
        "Agentes de IA",
      ];
      const selectedQuem = (debouncedFiltersForApi || []).filter((f) =>
        quemFilters.includes(f)
      );
      const multipleQuem = selectedQuem.length > 1;

      const tipoFilters = ["ChatBots", "Agentes de IA", "Fila"];
      const selectedTipo = (debouncedFiltersForApi || []).filter((f) =>
        tipoFilters.includes(f)
      );
      const singleTipo = selectedTipo.length === 1 ? selectedTipo[0] : null;
      const tipoMap = {
        ChatBots: "chatbots",
        "Agentes de IA": "agentes",
        Fila: "fila",
      };
      // Com 2+ opções em Quem, a lista usa OR no cliente; tipo_responsavel na API seria AND e esconderia resultados.
      const tipoResponsavel =
        !multipleQuem && singleTipo && tipoMap[singleTipo]
          ? tipoMap[singleTipo]
          : debouncedAdvancedFilterState.tipo_responsavel || null;

      const hasFila = (debouncedFiltersForApi || []).includes("Fila");
      // Fila no reader: janela curta evita scan enorme no scroll (cursor).
      const defaultDateWindowDays = hasFila ? 14 : 30;

      const advancedFromState = filterStateToAdvancedParams(debouncedAdvancedFilterState, {
        tipo_scope: isSalesPipelineSelected ? "todos" : "relacionamento",
        default_date_window_days: defaultDateWindowDays,
      });

      return {
        ...(advancedFromState || {
          tipo_scope: isSalesPipelineSelected ? "todos" : "relacionamento",
          default_date_window_days: defaultDateWindowDays,
        }),
        ...(tipoResponsavel && !(advancedFromState?.tipo_responsavel)
          ? { tipo_responsavel: tipoResponsavel }
          : {}),
      };
    })(),
    requestTotal,
    { lastSeenMap: lastSeenClientAtMap }
  );

  // Pedir total no banco só após a 1ª carga (evita COUNT pesado no primeiro request)
  useEffect(() => {
    if (data?.pages?.length > 0 && !hasRequestedTotal.current) {
      hasRequestedTotal.current = true;
      setRequestTotal(true);
    }
  }, [data?.pages?.length]);

  const conversationsFromApi = data?.pages?.flatMap((page) => page.list) ?? [];
  const conversationsWithUnread = conversationsFromApi.map((c) => ({
    ...c,
    unreadClient: hasUnreadClientMessage(c, lastSeenClientAtMap) ? 1 : 0,
  }));
  const totalInDatabase = data?.pages?.[0]?.totalInDatabase;

  const { data: meRoot } = useQuery(
    workspaceScopedQueryKey("me"),
    () => fetchData("/users/me"),
    {
      staleTime: 20000,
    }
  );
  const wpPersonalEnabled = Boolean(meRoot?.data?.whatsapp_personal?.enabled);

  const { data: personalChatsRaw = [] } = useQuery(
    workspaceScopedQueryKey("personal-whatsapp-chats", personalFilterOn, wpPersonalEnabled),
    () => getPersonalWhatsappChatsList(),
    {
      enabled: personalFilterOn && wpPersonalEnabled,
      refetchInterval: personalFilterOn && wpPersonalEnabled ? 10000 : false,
    }
  );

  const personalAsConversations = useMemo(() => {
    if (!personalFilterOn || !wpPersonalEnabled || !Array.isArray(personalChatsRaw)) return [];
    return personalChatsRaw.map((chat) => ({
      id: `wa-personal:${encodeURIComponent(chat.chatKey)}`,
      kind: "personal",
      personalChatKey: chat.chatKey,
      name: (chat.peerName && String(chat.peerName).trim()) || chat.peerPhone || chat.chatKey,
      nome: (chat.peerName && String(chat.peerName).trim()) || chat.peerPhone || chat.chatKey,
      profile_picture: chat.peerAvatar || null,
      lastMessage: chat.lastMessage,
      timestamp:
        chat.timestamp != null
          ? new Date(Number(chat.timestamp)).toISOString()
          : new Date().toISOString(),
      userId: userData?.id,
      assignedTo: userData?.nome,
      userName: userData?.nome,
      status: 1,
      statusLabel: "Em Atendimento",
      channel: "WhatsApp pessoal",
      channelLabel: "WhatsApp pessoal",
      channelType: "whatsapp",
      unreadClient: Number(chat.unreadClient) || 0,
      celular: chat.peerPhone
        ? toE164Digits(String(chat.peerPhone).replace(/\D/g, ""), "BR") ||
          String(chat.peerPhone).replace(/\D/g, "")
        : "",
    }));
  }, [personalFilterOn, wpPersonalEnabled, personalChatsRaw, userData]);

  const conversations = useMemo(() => {
    const merged = [...personalAsConversations, ...conversationsWithUnread];
    return merged.sort((a, b) => {
      const pa = a.pinned ? 1 : 0;
      const pb = b.pinned ? 1 : 0;
      if (pb !== pa) return pb - pa;
      const ta = new Date(a.timestamp || 0).getTime();
      const tb = new Date(b.timestamp || 0).getTime();
      if (tb !== ta) return tb - ta;
      return String(b.id).localeCompare(String(a.id));
    });
  }, [personalAsConversations, conversationsWithUnread]);

  const conversationsTimeoutMessage =
    (data?.pages || []).map((p) => p?.timeoutMessage).find(Boolean) || null;

  // Throttle para evitar spam de refetch quando chegam muitos eventos socket.
  const lastListRefreshAtRef = useRef(0);
  const listRefreshCooldownMs = 8000;
  const listRefreshRefetch = useCallback(() => {
    const now = Date.now();
    if (now - lastListRefreshAtRef.current < listRefreshCooldownMs) return;
    lastListRefreshAtRef.current = now;
    if (typeof refetch === "function") refetch();
  }, [refetch]);

  // Reconcile do histórico usando o endpoint paginado correto.
  // Mantém mensagens já carregadas (merge idempotente) para não quebrar paginação/scroll.
  // Lock por conversationId (não global) para A não bloquear sync de B.
  const messagesSyncInFlightRef = useRef(new Set());
  const syncLatestConversationMessagesById = useCallback(
    async (conversationId, { limit = 50 } = {}) => {
    if (!conversationId) return;
    const syncKey = String(conversationId);
    if (messagesSyncInFlightRef.current.has(syncKey)) return;
    messagesSyncInFlightRef.current.add(syncKey);

    const pKey = getPersonalChatKeyFromSyntheticConversationId(conversationId);
    if (pKey) {
      try {
        const payload = await getPersonalWhatsappChatMessages(pKey);
        if (!isActiveConversationId(conversationId)) return;
        const list = Array.isArray(payload?.messages) ? payload.messages : [];
        applyMessagesIfActive(conversationId, (prev) => {
          const serverUi = list
            .map((m) =>
              tagMessageConversationId(mapPersonalRedisMessageToUi(m, userData), conversationId)
            )
            .filter((u) => u?.id != null);
          const next = new Map(prev);
          for (const ui of serverUi) next.set(ui.id, ui);
          for (const [key, msg] of [...next.entries()]) {
            if (msg?.conversationId != null && String(msg.conversationId) !== syncKey) continue;
            if (personalPendingSupersededByServerList(serverUi, key, msg, userData)) {
              next.delete(key);
            }
          }
          return next;
        });
      } catch (err) {
        console.error("Erro ao sincronizar mensagens (WhatsApp pessoal):", err);
      } finally {
        messagesSyncInFlightRef.current.delete(syncKey);
      }
      return;
    }
    try {
      const payload = await getConversationMessagesById(String(conversationId), {
        limit,
        returnRaw: true,
      });

      if (!isActiveConversationId(conversationId)) return;

      const list = Array.isArray(payload?.messages)
        ? payload.messages
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

      setMessagesSyncError(null);

      applyMessagesIfActive(conversationId, (prev) => {
        const next = new Map(prev);

        const mergeKeepingLocalMedia = (prevMsg, incoming) => {
          if (!prevMsg) return incoming;
          const merged = { ...prevMsg, ...incoming };
          const keepIfMissing = ["image", "audio", "video", "file", "document", "location"];
          keepIfMissing.forEach((k) => {
            if (
              (merged[k] === null || merged[k] === undefined || merged[k] === "") &&
              prevMsg[k]
            ) {
              merged[k] = prevMsg[k];
            }
          });
          return merged;
        };

        for (const msg of list) {
          if (!msg || msg.id == null) continue;
          const existing = next.get(msg.id);
          const enriched = tagMessageConversationId(
            enrichMessageLocationFromApi(msg),
            conversationId
          );
          next.set(msg.id, mergeKeepingLocalMedia(existing, enriched));
        }

        for (const [key, msg] of [...next.entries()]) {
          if (msg?.conversationId != null && String(msg.conversationId) !== syncKey) continue;
          if (regularConversationPendingSupersededByServer(list, key, msg, userData)) {
            next.delete(key);
          }
        }

        return next;
      });
    } catch (err) {
      console.error("Erro ao sincronizar mensagens (realtime/polling):", err);
      if (!isActiveConversationId(conversationId)) return;
      applyMessagesFetchError(err, conversationId, {
        mergeSyncError: true,
        fallbackMessage: "Não foi possível atualizar o histórico.",
      });
    } finally {
      messagesSyncInFlightRef.current.delete(syncKey);
    }
    },
    [
      userData,
      isActiveConversationId,
      applyMessagesIfActive,
      tagMessageConversationId,
      applyMessagesFetchError,
    ]
  );

  const markClientMessagesAsSeen = (conversation) => {
    if (!conversation) return;
    const convId = conversation.id;
    const lastClientAt = conversation.lastClientMessageAt;
    if (!convId || !lastClientAt) return;

    setLastSeenClientAtMap((prev) => {
      const next = {
        ...prev,
        [convId]: lastClientAt,
      };
      try {
        writeWorkspaceLocalJson(WS_KEY.CHAT_LAST_SEEN, next);
      } catch (e) {
        console.warn("Falha ao salvar lastSeenClientAt no localStorage", e);
      }
      queryClient.invalidateQueries(WORKSPACE_RAIL_COUNTS_KEY);
      return next;
    });
  };

  const loadAiInsights = async (conversationIdParam) => {
    const conversationIdToUse = conversationIdParam || selectedConversationId;
    if (!conversationIdToUse) return;
    if (getPersonalChatKeyFromSyntheticConversationId(conversationIdToUse)) return;

    // Se já temos insights para esta conversa específica, não recarrega
    if (
      aiInsights &&
      aiInsightsConversationId != null &&
      String(aiInsightsConversationId) === String(conversationIdToUse)
    ) {
      return;
    }

    const requestConversationId = String(conversationIdToUse);

    try {
      setIsLoadingInsights(true);
      setAiInsightsError(null);

      const data = await getConversationInsights(requestConversationId);

      // Se, quando a resposta chegou, já mudamos de conversa, ignora este resultado
      if (String(selectedConversationId) !== String(requestConversationId)) {
        return;
      }

      setAiInsights(data || null);
      setAiInsightsConversationId(requestConversationId);

      // também grava no objeto da conversa atual para reaproveitar ao reabrir o acordeão
      setSelectedConversation((prev) => {
        if (!prev || prev.id !== requestConversationId) return prev;
        return { ...prev, aiInsights: data };
      });
    } catch (err) {
      console.error("Erro ao carregar insights da conversa:", err);

      // só aplica o erro se ainda estivermos na mesma conversa
      if (String(selectedConversationId) === String(requestConversationId)) {
        setAiInsightsError(err?.message || "Erro ao carregar insights da conversa.");
      }
    } finally {
      if (String(selectedConversationId) === String(requestConversationId)) {
        setIsLoadingInsights(false);
      }
    }
  };

  useRealtimeMessages({
    selectedConversationId,
    refetchConversationDetails,
    refetchConversations: listRefreshRefetch,
    refreshConversationMessages: syncLatestConversationMessagesById,
    onSocketActivity,
  });

  useEffect(() => {
    if (!detailsAccessDenied) return;
    setSelectedConversation(null);
  }, [detailsAccessDenied, selectedConversationId]);

  useEffect(() => {
    if (!conversationDetails?.data) return;
    if (detailsAccessDenied) return;

    const d = conversationDetails.data; // payload do endpoint /conversations/:id
    // Backend pode enviar agente no objeto principal ou aninhado (ex.: request, solicitation)
    const req = d.request ?? d.solicitation ?? d.demand ?? d;

    const rawTypeType = d.typeType ?? req.typeType ?? null;

    const agentId =
      d.agentId ?? d.id_agente ?? req.agentId ?? req.id_agente ?? null;
    const agentName =
      d.agentName ?? d.agent_name ?? req.agentName ?? req.agent_name ?? null;
    const client = d.client ?? null;
    // normalização única para a UI:
    const normalized = {
      id: d.id,
      status: d.status,
      statusLabel: getStatusLabelFromCode(d.status),
      projectId:
        d.projectId ?? d.id_projeto ?? req.projectId ?? req.id_projeto ?? null,
      channelId:
        d.channelId ?? d.id_origem ?? req.channelId ?? req.id_origem ?? null,
      typeId: d.typeId ?? d.id_tipo ?? req.typeId ?? req.id_tipo ?? null,
      typeType: rawTypeType,
      typeName: d.typeName ?? d.tipo_nome ?? req.typeName ?? req.tipo_nome ?? null,
      typeStatus: d.typeStatus ?? req.typeStatus ?? null,
      typeExcluido: d.typeExcluido ?? req.typeExcluido ?? 0,
      subtype1:
        d.subtype1 ?? d.id_subtipo1 ?? req.subtype1 ?? req.id_subtipo1 ?? null,
      subtype1Name:
        d.subtype1Name ?? d.subtipo1_nome ?? req.subtype1Name ?? req.subtipo1_nome ?? null,
      subtype1Status: d.subtype1Status ?? req.subtype1Status ?? null,
      subtype1Excluido: d.subtype1Excluido ?? req.subtype1Excluido ?? 0,
      subtype2:
        d.subtype2 ?? d.id_subtipo2 ?? req.subtype2 ?? req.id_subtipo2 ?? null,
      subtype2Name:
        d.subtype2Name ?? d.subtipo2_nome ?? req.subtype2Name ?? req.subtipo2_nome ?? null,
      subtype2Status: d.subtype2Status ?? req.subtype2Status ?? null,
      subtype2Excluido: d.subtype2Excluido ?? req.subtype2Excluido ?? 0,
      subtype3:
        d.subtype3 ?? d.id_subtipo3 ?? req.subtype3 ?? req.id_subtipo3 ?? null,
      subtype3Name:
        d.subtype3Name ?? d.subtipo3_nome ?? req.subtype3Name ?? req.subtipo3_nome ?? null,
      subtype3Status: d.subtype3Status ?? req.subtype3Status ?? null,
      subtype3Excluido: d.subtype3Excluido ?? req.subtype3Excluido ?? 0,
      typeClassificationSource:
        d.typeClassificationSource ??
        d.tipo_classificacao_fonte ??
        req.typeClassificationSource ??
        "default",
      typeSuggestion: d.typeSuggestion ?? req.typeSuggestion ?? null,
      userId:
        d.userId ?? d.id_usuario ?? req.userId ?? req.id_usuario ?? null,
      userName: d.userName ?? req.userName ?? null,
      agentId,
      agentName,
      potential:
        d.potential ?? d.potencial ?? req.potential ?? req.potencial ?? null,
      nextContactAt:
        d.nextContactAt ??
        d.dataproximocontato ??
        req.nextContactAt ??
        req.dataproximocontato ??
        null,
      nf: d.nf ?? req.nf ?? null,
      produto: d.produto ?? req.produto ?? null,
      campanha: d.campanha ?? req.campanha ?? null,
      texto_adicional: d.texto_adicional ?? req.texto_adicional ?? null,
      data_adicional: d.data_adicional ?? req.data_adicional ?? null,
      valor: d.valor ?? req.valor ?? null,
      customFields: d.customFields ?? req.customFields ?? {},

      // mapeia “cliente” para os nomes esperados pelos componentes
      clientId: d.clientId ?? null,
      ...(d.relationshipsCount != null || d.relationships_count != null
        ? {
            relationshipsCount:
              Number(d.relationshipsCount ?? d.relationships_count) || 0,
          }
        : {}),
      ...(d.relationshipHint != null || d.relationship_hint != null
        ? {
            relationshipHint: d.relationshipHint ?? d.relationship_hint,
          }
        : {}),
      nome: client?.name ?? "",
      email: client?.email ?? "",
      celular: sanitizeStoredPhoneForDisplay(client?.mobile ?? client?.phone ?? "") || "",
      whatsappPhone: sanitizeStoredPhoneForDisplay(client?.whatsappPhone ?? "") || "",
      telefone: sanitizeStoredPhoneForDisplay(client?.telephone ?? "") || "",
      whatsapp:
        sanitizeStoredPhoneForDisplay(client?.whatsapp ?? "") ||
        sanitizeStoredPhoneForDisplay(client?.whatsappPhone ?? "") ||
        "",
      cpf: client?.cpf ?? "",
      cnpj: client?.cnpj ?? "",
      razaosocial: client?.companyName ?? "",
      identidade: client?.identityDocument ?? "",
      data_nascimento: client?.birthDate ?? null,
      data_abertura: client?.openingDate ?? null,
      nome_responsavel: client?.responsibleName ?? "",
      cargo_responsavel: client?.responsibleRole ?? "",
      tamanho_frota: client?.fleetSize ?? "",
      inscricao_estadual: client?.stateRegistration ?? "",
      inscricao_estadual_isento: Number(client?.stateRegistrationExempt) ? 1 : 0,
      sexo: client?.gender ?? null,
      id_facebook: client?.facebook ?? "",
      id_instagram: client?.instagram ?? "",
      id_linkedin: client?.linkedin ?? "",
      clientType: client?.clientType ?? null,
      id_interno: client?.id_interno ?? d.id_interno ?? null,
      cep: client?.zip ?? "",
      endereco: client?.address ?? "",
      numero: client?.addressNumber ?? "",
      complemento: client?.addressComplement ?? "",
      bairro: client?.neighborhood ?? "",
      cidade: client?.city ?? client?.locality ?? null,
      uf: client?.state ?? null,
      clientPotential: client?.potential ?? null,
      // Se o cliente atual não vier com grupo/tags, NÃO herdar do anterior.
      id_grupo: client?.id_grupo ?? null,
      clientTags: client?.tags ?? "",
      campo_adicional1_sim_nao:
        client?.campo_adicional1_sim_nao ?? d.client?.campo_adicional1_sim_nao ?? 0,
      campo_adicional2_sim_nao:
        client?.campo_adicional2_sim_nao ?? d.client?.campo_adicional2_sim_nao ?? 0,
      campo_adicional3_multiselect:
        client?.campo_adicional3_multiselect ?? d.client?.campo_adicional3_multiselect ?? "",
      clientCustomFields: client?.customFields ?? d.client?.customFields ?? {},
      tags: d.tags ?? null,
      metaReferral: d.metaReferral ?? null,
      whatsappAttribution: d.whatsappAttribution ?? d.metaReferral ?? null,

      // channel string (ex.: “Ecommerce”)
      channel: d.channel ?? null,
      channelType: d.channelType ?? d.channel_type ?? null,

      // messages para o histórico (carregadas separadamente via /conversations/:id/messages)
      messages: Array.isArray(d.messages) ? d.messages : [],
      products: Array.isArray(d.products) ? d.products : [],
      history: Array.isArray(d.history) ? d.history : [],
      messagingWindows: d.messagingWindows ?? d.messaging_windows ?? null,
      webchatAtivo:
        d.webchatAtivo === true ||
        d.webchatAtivo === 1 ||
        d.webchatAtivo === "1",
      webchatOpenedAt: d.webchatOpenedAt ?? null,
      webchatRedirectEnabled: Boolean(d.webchatRedirectEnabled),
      webchatPresence: d.webchatPresence || null,
      aiInsights,
    };

    // Evita colar dados de outra conversa ou de outro workspace (mesmo id numérico).
    setSelectedConversation((prev) => {
      const prevId = prev?.id != null ? String(prev.id) : null;
      const nextId = normalized?.id != null ? String(normalized.id) : null;
      const prevWs = prev?.__workspaceSlug ?? null;
      const nextWs = workspaceSlug ?? null;
      if (
        prevId &&
        nextId &&
        prevId === nextId &&
        prevWs &&
        nextWs &&
        prevWs === nextWs
      ) {
        return { ...(prev || {}), ...normalized, __workspaceSlug: nextWs };
      }
      return { ...normalized, __workspaceSlug: nextWs };
    });
    // Loading da UI do histórico termina no load de mensagens (loadInitialMessages),
    // não aqui — evita pintar painel enquanto um fetch antigo ainda pode misturar.
  }, [conversationDetails, workspaceSlug, detailsAccessDenied]);

  useEffect(() => {
    if (prevWorkspaceSlugRef.current === workspaceSlug) return;
    prevWorkspaceSlugRef.current = workspaceSlug;
    setSelectedConversation(null);
    setMessagesAccessDenied(null);
    setMessagesMap(new Map());
    setMessagesPageState({
      hasMore: false,
      lastMessageId: null,
      isInitialLoaded: false,
      isLoadingMore: false,
    });
    setAiInsights(null);
    setAiInsightsError(null);
    setAiInsightsConversationId(null);
    if (selectedConversationId) {
      setIsLoadingConversation(true);
    }
  }, [workspaceSlug, selectedConversationId]);

  useEffect(() => {
    const isChatRoute = isServiceChatPath(location.pathname);
    if (!isChatRoute) return;

    if (id) {
      setSelectedConversationId(id);
      // Mobile: abrir pela URL (ex.: notificação → /chat/:id) precisa espelhar o mesmo
      // layout que um toque na lista; senão o histórico fica oculto com showMessageHistory=false.
      if (popupCallMode) {
        setShowConversationList(false);
        setShowMessageHistory(true);
        setShowConversationDetails(true);
      } else if (isMobile) {
        setShowMessageHistory(true);
        setShowConversationList(false);
        setShowConversationDetails(false);
      }
    }
  }, [id, location.pathname, isMobile, popupCallMode]);

  // Ao trocar de conversa: limpa insights e marca carregamento. Não zera `selectedConversation`
  // se já reflete o mesmo id no mesmo workspace (ex.: clique na lista acabou de preencher com o item da fila) —
  // senão há um frame “vazio” até o GET /conversations/:id concluir.
  useEffect(() => {
    if (!selectedConversationId) return;
    setIsLoadingConversation(true);
    setMessagesAccessDenied(null);
    setSelectedConversation((prev) => {
      if (prev == null) return null;
      if (String(prev.id) !== String(selectedConversationId)) return null;
      if (prev.__workspaceSlug && workspaceSlug && prev.__workspaceSlug !== workspaceSlug) {
        return null;
      }
      return prev;
    });
    setAiInsights(null);
    setAiInsightsError(null);
    setAiInsightsConversationId(null);
  }, [selectedConversationId, workspaceSlug]);

  // Carrega a primeira página de mensagens sempre que uma nova conversa é selecionada
  useEffect(() => {
    const loadInitialMessages = async () => {
      if (!selectedConversationId) return;
      const requestId = String(selectedConversationId);
      setMessagesSyncError(null);
      setMessagesPageState((prev) => ({
        ...prev,
        isInitialLoaded: false,
        isLoadingMore: true,
      }));
      try {
        const pKey = getPersonalChatKeyFromSyntheticConversationId(selectedConversationId);
        if (pKey) {
          const payload = await getPersonalWhatsappChatMessages(pKey);
          if (!isActiveConversationId(requestId)) return;
          const list = Array.isArray(payload?.messages) ? payload.messages : [];
          applyMessagesIfActive(requestId, (prev) => {
            const serverUi = list
              .map((m) =>
                tagMessageConversationId(mapPersonalRedisMessageToUi(m, userData), requestId)
              )
              .filter((u) => u?.id != null);
            const next = new Map();
            for (const ui of serverUi) next.set(ui.id, ui);
            prev.forEach((msg, key) => {
              if (String(msg?.conversationId) !== requestId) return;
              const isPending =
                String(key).startsWith("temp-") ||
                msg.status === "sending" ||
                msg.status === "error";
              if (!isPending) return;
              if (personalPendingSupersededByServerList(serverUi, key, msg, userData)) return;
              if (!next.has(key)) next.set(key, msg);
            });
            return next;
          });
          if (!isActiveConversationId(requestId)) return;
          setMessagesPageState({
            hasMore: false,
            lastMessageId: null,
            isInitialLoaded: true,
            isLoadingMore: false,
          });
          setIsLoadingConversation(false);
          setMessagesSyncError(null);
          return;
        }

        const payload = await getConversationMessagesById(selectedConversationId, {
          limit: 50,
          returnRaw: true,
        });
        if (!isActiveConversationId(requestId)) return;
        const list = Array.isArray(payload.messages)
          ? payload.messages
          : Array.isArray(payload.data)
            ? payload.data
            : [];

        applyMessagesIfActive(requestId, (prev) => {
          const next = new Map();
          for (const msg of list) {
            next.set(
              msg.id,
              tagMessageConversationId(enrichMessageLocationFromApi(msg), requestId)
            );
          }
          // preserva apenas pendentes desta mesma solicitação
          prev.forEach((msg, key) => {
            if (String(msg?.conversationId) !== requestId) return;
            const isPending =
              String(key).startsWith("temp-") ||
              msg.status === "sending" ||
              msg.status === "error";
            if (!isPending) return;
            if (regularConversationPendingSupersededByServer(list, key, msg, userData)) return;
            if (!next.has(key)) next.set(key, msg);
          });
          return next;
        });

        if (!isActiveConversationId(requestId)) return;
        const pagination = payload.pagination || {};
        setMessagesPageState({
          hasMore: Boolean(pagination.hasMore),
          lastMessageId: pagination.lastMessageId ?? null,
          isInitialLoaded: true,
          isLoadingMore: false,
        });
        setIsLoadingConversation(false);
        setMessagesSyncError(null);
      } catch (err) {
        console.error("Erro ao carregar mensagens da conversa:", err);
        if (!isActiveConversationId(requestId)) return;
        applyMessagesFetchError(err, requestId, {
          fallbackMessage:
            "Não foi possível carregar o histórico desta conversa. Tente novamente.",
        });
        setMessagesPageState((prev) => ({
          ...prev,
          isLoadingMore: false,
          isInitialLoaded: true,
        }));
        setIsLoadingConversation(false);
      }
    };

    // ao trocar de conversa, limpa mensagens anteriores antes de carregar
    setMessagesMap(new Map());
    if (selectedConversationId) {
      loadInitialMessages();
    }
  }, [
    selectedConversationId,
    userData,
    isActiveConversationId,
    applyMessagesIfActive,
    tagMessageConversationId,
    applyMessagesFetchError,
  ]);

  // Reconciliação por polling (rede de segurança quando o socket não chega).
  // Rodamos apenas quando a aba está visível.
  const pagesLenRef = useRef(data?.pages?.length ?? 0);
  useEffect(() => {
    pagesLenRef.current = data?.pages?.length ?? 0;
  }, [data?.pages?.length]);

  useEffect(() => {
    const isChatRoute = isServiceChatPath(location.pathname);
    if (!isChatRoute) return;

    if (typeof document === "undefined") return;

    const runHistorySync = () => {
      if (document.visibilityState !== "visible") return;
      const activeId = selectedConversationIdRef.current;
      if (activeId) syncLatestConversationMessagesById(activeId, { limit: 50 });
    };

    const runListSync = () => {
      if (document.visibilityState !== "visible") return;
      // Evita churn na paginação infinita: só refaz quando normalmente temos 1 página.
      if ((pagesLenRef.current ?? 0) <= 1) listRefreshRefetch();
    };

    const syncNow = () => {
      runHistorySync();
      runListSync();
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") syncNow();
    };

    window.addEventListener("focus", syncNow);
    document.addEventListener("visibilitychange", handleVisibility);

    const historyIntervalMs = 12000;
    const listIntervalMs = 30000;

    const historyTimer = setInterval(runHistorySync, historyIntervalMs);
    const listTimer = setInterval(runListSync, listIntervalMs);

    // Um sync inicial ao entrar/voltar para a rota de chat.
    syncNow();

    return () => {
      window.removeEventListener("focus", syncNow);
      document.removeEventListener("visibilitychange", handleVisibility);
      clearInterval(historyTimer);
      clearInterval(listTimer);
    };
  }, [
    location.pathname,
    listRefreshRefetch,
    syncLatestConversationMessagesById,
  ]);

  useEffect(() => {
    if (!isLoading) setInitialLoading(false);
  }, [isLoading]);

  useEffect(() => {
    if (getPersonalChatKeyFromSyntheticConversationId(selectedConversationId)) {
      setShowConversationDetails(false);
    }
  }, [selectedConversationId]);

  const deletePersonalConversation = useCallback(async () => {
    const pKey = getPersonalChatKeyFromSyntheticConversationId(selectedConversationId);
    if (!pKey) throw new Error("chatKey ausente");
    await deletePersonalWhatsappChat(pKey);
    await queryClient.invalidateQueries({ queryKey: ["personal-whatsapp-chats"] });
    setSelectedConversation(null);
    setSelectedConversationId(null);
    setMessagesMap(new Map());
    setAiInsights(null);
    setAiInsightsError(null);
    setAiInsightsConversationId(null);
    setMessagesPageState({
      hasMore: false,
      lastMessageId: null,
      isInitialLoaded: false,
      isLoadingMore: false,
    });
    setMessagesSyncError(null);
    setMessagesAccessDenied(null);
    setIsLoadingConversation(false);
    navigateChat("/chat");
    if (isMobile) {
      setShowConversationList(true);
      setShowMessageHistory(false);
    }
    setShowConversationDetails(false);
  }, [selectedConversationId, queryClient, navigateChat, isMobile]);

  const handleSelectConversation = (conversation) => {
    if (!conversation?.id) return;

    const nextId = String(conversation.id);
    const isSameConversation =
      selectedConversationId != null &&
      String(selectedConversationId) === nextId;

    if (isSameConversation && detailsAccessDenied) {
      setMessagesSyncError(null);
      setIsLoadingConversation(false);
      navigateChat(`/chat/${encodeURIComponent(nextId)}`);
      return;
    }

    // Sempre atualiza dados básicos e marca mensagens do cliente como lidas.
    // Ao trocar de id: substitui o objeto (não faz spread de prev) para não vazar
    // email/telefone/clientId/canal da solicitação anterior.
    setSelectedConversation((prev) => {
      const prevId = prev?.id != null ? String(prev.id) : null;
      if (prevId === nextId) {
        const base = { ...(prev || {}), ...conversation };
        const hasClientTags =
          conversation?.clientTags != null ||
          conversation?.client?.tags != null;
        const hasClientGroup =
          conversation?.id_grupo != null ||
          conversation?.client?.id_grupo != null;
        const hasConversationTags = conversation?.tags != null;

        return {
          ...base,
          ...(hasClientTags ? {} : { clientTags: "" }),
          ...(hasClientGroup ? {} : { id_grupo: null }),
          ...(hasConversationTags ? {} : { tags: null }),
          __workspaceSlug: workspaceSlug ?? prev?.__workspaceSlug ?? null,
        };
      }
      return {
        ...conversation,
        __workspaceSlug: workspaceSlug ?? null,
      };
    });
    markClientMessagesAsSeen(conversation);

    const isPersonalWa =
      conversation.kind === "personal" ||
      String(conversation.id || "").startsWith("wa-personal:");
    if (isPersonalWa) {
      setShowConversationDetails(false);
    }

    if (isMobile) {
      setShowMessageHistory(true);
      setShowConversationList(false);
      setShowConversationDetails(false);
    }

    // Se for a mesma conversa, apenas garante a navegação/visibilidade,
    // sem forçar estado de "carregando" infinito.
    if (isSameConversation) {
      setMessagesSyncError(null);
      setIsLoadingConversation(false);
      navigateChat(`/chat/${encodeURIComponent(nextId)}`);
      return;
    }

    // Nova conversa: dispara carregamento de detalhes e mensagens
    setMessagesSyncError(null);
    setMessagesAccessDenied(null);
    setIsLoadingConversation(true);
    setAiInsights(null);
    setAiInsightsError(null);
    setAiInsightsConversationId(null);
    setSelectedConversationId(nextId);
    navigateChat(`/chat/${encodeURIComponent(nextId)}`);
  };

  const reloadConversationMessages = useCallback(async () => {
    const cid = selectedConversationId;
    if (!cid) return;
    const requestId = String(cid);
    setMessagesSyncError(null);
    setMessagesPageState((prev) => ({
      ...prev,
      isInitialLoaded: false,
      isLoadingMore: true,
    }));
    try {
      const pKey = getPersonalChatKeyFromSyntheticConversationId(cid);
      if (pKey) {
        const payload = await getPersonalWhatsappChatMessages(pKey);
        if (!isActiveConversationId(requestId)) return;
        const list = Array.isArray(payload?.messages) ? payload.messages : [];
        applyMessagesIfActive(requestId, (prev) => {
          const serverUi = list
            .map((m) =>
              tagMessageConversationId(mapPersonalRedisMessageToUi(m, userData), requestId)
            )
            .filter((u) => u?.id != null);
          const next = new Map();
          for (const ui of serverUi) next.set(ui.id, ui);
          prev.forEach((msg, key) => {
            if (String(msg?.conversationId) !== requestId) return;
            const isPending =
              String(key).startsWith("temp-") ||
              msg.status === "sending" ||
              msg.status === "error";
            if (!isPending) return;
            if (personalPendingSupersededByServerList(serverUi, key, msg, userData)) return;
            if (!next.has(key)) next.set(key, msg);
          });
          return next;
        });
        if (!isActiveConversationId(requestId)) return;
        setMessagesPageState({
          hasMore: false,
          lastMessageId: null,
          isInitialLoaded: true,
          isLoadingMore: false,
        });
        return;
      }

      const payload = await getConversationMessagesById(cid, {
        limit: 50,
        returnRaw: true,
      });
      if (!isActiveConversationId(requestId)) return;
      const list = Array.isArray(payload.messages)
        ? payload.messages
        : Array.isArray(payload.data)
          ? payload.data
          : [];

      applyMessagesIfActive(requestId, (prev) => {
        const next = new Map();
        for (const msg of list) {
          next.set(
            msg.id,
            tagMessageConversationId(enrichMessageLocationFromApi(msg), requestId)
          );
        }
        prev.forEach((msg, key) => {
          if (String(msg?.conversationId) !== requestId) return;
          const isPending =
            String(key).startsWith("temp-") ||
            msg.status === "sending" ||
            msg.status === "error";
          if (!isPending) return;
          if (regularConversationPendingSupersededByServer(list, key, msg, userData)) return;
          if (!next.has(key)) next.set(key, msg);
        });
        return next;
      });

      if (!isActiveConversationId(requestId)) return;
      const pagination = payload.pagination || {};
      setMessagesPageState({
        hasMore: Boolean(pagination.hasMore),
        lastMessageId: pagination.lastMessageId ?? null,
        isInitialLoaded: true,
        isLoadingMore: false,
      });
    } catch (err) {
      console.error("Erro ao recarregar mensagens da conversa:", err);
      if (!isActiveConversationId(requestId)) return;
      applyMessagesFetchError(err, requestId, {
        fallbackMessage:
          "Não foi possível carregar o histórico desta conversa. Tente novamente.",
      });
      setMessagesPageState((prev) => ({
        ...prev,
        isLoadingMore: false,
        isInitialLoaded: true,
      }));
    }
  }, [
    selectedConversationId,
    userData,
    isActiveConversationId,
    applyMessagesIfActive,
    tagMessageConversationId,
    applyMessagesFetchError,
  ]);

  const loadOlderMessages = async () => {
    if (!selectedConversationId) return;
    const requestId = String(selectedConversationId);
    if (getPersonalChatKeyFromSyntheticConversationId(selectedConversationId)) return;
    if (messagesPageState.isLoadingMore || !messagesPageState.hasMore) return;

    const currentMessages = Array.from(messagesMap.values()).filter(
      (m) => String(m?.conversationId) === requestId
    );
    if (!currentMessages.length) return;

    const sortedByIdAsc = [...currentMessages].sort((a, b) => {
      const idA = typeof a.id === "number" ? a.id : Number(a.id) || 0;
      const idB = typeof b.id === "number" ? b.id : Number(b.id) || 0;
      return idA - idB;
    });
    const oldest = sortedByIdAsc[0];
    if (!oldest?.id) return;

    setMessagesPageState((prev) => ({ ...prev, isLoadingMore: true }));

    try {
      const payload = await getConversationMessagesById(selectedConversationId, {
        limit: 50,
        beforeMessageId: oldest.id,
        returnRaw: true,
      });
      if (!isActiveConversationId(requestId)) return;
      const list = Array.isArray(payload.messages)
        ? payload.messages
        : Array.isArray(payload.data)
          ? payload.data
          : [];

      applyMessagesIfActive(requestId, (prev) => {
        const next = new Map(prev);
        for (const msg of list) {
          if (!next.has(msg.id)) {
            next.set(
              msg.id,
              tagMessageConversationId(enrichMessageLocationFromApi(msg), requestId)
            );
          }
        }
        return next;
      });

      if (!isActiveConversationId(requestId)) return;
      const pagination = payload.pagination || {};
      setMessagesPageState((prev) => ({
        ...prev,
        hasMore: Boolean(pagination.hasMore),
        lastMessageId: pagination.lastMessageId ?? prev.lastMessageId,
        isLoadingMore: false,
      }));
    } catch (err) {
      console.error("Erro ao carregar mensagens antigas:", err);
      if (!isActiveConversationId(requestId)) return;
      applyMessagesFetchError(err, requestId, {
        duplicatesOnly: true,
        fallbackMessage:
          "Não foi possível carregar mensagens mais antigas por conflito de cadastro do cliente.",
      });
      setMessagesPageState((prev) => ({ ...prev, isLoadingMore: false }));
    }
  };

  const handleEndConversation = () => {
    alert("Conversa encerrada");
    setSelectedConversation(null);
    setMessagesMap(new Map());
    setMessagesSyncError(null);
    setMessagesAccessDenied(null);
    if (isMobile) {
      setShowConversationList(true);
      setShowMessageHistory(false);
    }
    navigateChat("/chat");
  };

  const handleDeleteMessage = useCallback(
    async (messageId) => {
      if (!selectedConversationId || messageId == null) return;
      const requestId = String(selectedConversationId);
      if (getPersonalChatKeyFromSyntheticConversationId(selectedConversationId)) return;
      const mid = typeof messageId === "number" ? messageId : Number(messageId);
      if (!Number.isFinite(mid)) return;
      const result = await deleteConversationMessage(selectedConversationId, mid);
      if (!isActiveConversationId(requestId)) return;
      if (!result?.deletedAt && !result?.data?.deletedAt && result?.ok !== true) return;
      applyMessagesIfActive(requestId, (prev) => {
        const next = new Map(prev);
        const current = next.get(mid);
        if (current) {
          next.set(mid, {
            ...current,
            deletedAt: result?.deletedAt || result?.data?.deletedAt || new Date().toISOString(),
            canDeleteFromPlatform: false,
          });
        }
        return next;
      });
    },
    [selectedConversationId, isActiveConversationId, applyMessagesIfActive]
  );

  const handleReactMessage = useCallback(
    async (messageId, emoji) => {
      if (!selectedConversationId || messageId == null) return;
      await reactConversationMessage(selectedConversationId, messageId, emoji);
    },
    [selectedConversationId]
  );

  const handleEditWhatsappMessage = useCallback(
    async (messageId, content) => {
      if (!selectedConversationId || messageId == null) return;
      await editConversationWhatsappMessage(selectedConversationId, messageId, content);
      applyMessagesIfActive(String(selectedConversationId), (prev) => {
        const next = new Map(prev);
        const current = next.get(Number(messageId));
        if (current) next.set(Number(messageId), { ...current, text: content });
        return next;
      });
    },
    [selectedConversationId, applyMessagesIfActive]
  );

  const handleForwardWhatsappMessage = useCallback(
    async (messageId, to) => {
      if (!selectedConversationId || messageId == null) return;
      await forwardConversationWhatsappMessage(selectedConversationId, messageId, to);
    },
    [selectedConversationId]
  );

  const handleEditNote = useCallback(
    async (messageId, content) => {
      if (!selectedConversationId || messageId == null) return null;
      const requestId = String(selectedConversationId);
      const mid = Number(messageId);
      if (!Number.isFinite(mid)) return null;
      const result = await editConversationNote(selectedConversationId, mid, content);
      if (isActiveConversationId(requestId)) {
        applyMessagesIfActive(requestId, (prev) => {
          const next = new Map(prev);
          const current = next.get(mid);
          if (current) {
            next.set(mid, {
              ...current,
              text: result.content ?? content,
              editedAt: result.editedAt ?? current.editedAt ?? null,
              editHistory: result.editHistory ?? current.editHistory ?? [],
              updatedAt: result.editedAt ?? current.updatedAt,
            });
          }
          return next;
        });
      }
      return result;
    },
    [selectedConversationId, isActiveConversationId, applyMessagesIfActive]
  );

  const handleDeleteNote = useCallback(
    async (messageId) => {
      if (!selectedConversationId || messageId == null) return null;
      const requestId = String(selectedConversationId);
      const mid = Number(messageId);
      if (!Number.isFinite(mid)) return null;
      const result = await deleteConversationNote(selectedConversationId, mid);
      if (isActiveConversationId(requestId)) {
        applyMessagesIfActive(requestId, (prev) => {
          const next = new Map(prev);
          const current = next.get(mid);
          if (current) {
            next.set(mid, {
              ...current,
              deletedAt: result.deletedAt || new Date().toISOString(),
              canEditNote: false,
              canDeleteNote: false,
            });
          }
          return next;
        });
      }
      return result;
    },
    [selectedConversationId, isActiveConversationId, applyMessagesIfActive]
  );

  const containerStyle = useMemo(() => {
    if (
      isPopupCallPath(location.pathname) ||
      isPopupServiceChatPath(location.pathname) ||
      (isMobile && isWorkspaceNavSearch(location.search))
    ) {
      return getTeamPaneContainerStyle(theme, true);
    }
    return {
      boxShadow:
        theme.palette.mode === "dark"
          ? "0 1px 4px rgba(0, 0, 0, 0.5)"
          : "0 1px 4px rgba(0, 0, 0, 0.1)",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: "8px",
      overflow: "hidden",
    };
  }, [theme, isMobile, location.pathname, location.search]);

  const applyChatFilterState = useCallback((state) => {
    const normalized = normalizeSearchFilterState(state);
    if (Array.isArray(normalized.filters)) {
      setFilters(normalized.filters);
    }
    if (typeof normalized.q === "string") {
      setSearchTerm(normalized.q.trim());
    }
    setAdvancedFilterState(normalized);
  }, []);

  const clearAdvancedFilterState = useCallback(() => {
    setAdvancedFilterState(EMPTY_SEARCH_FILTER_STATE);
  }, []);

  return {
    theme,
    isMobile,
    userData,
    selectedConversationId,
    selectedConversation,
    messages,
    showConversationList,
    showMessageHistory,
    showConversationDetails,
    isLoadingDetails,
    initialLoading,
    isLoadingConversation,
    messagesSyncError,
    reloadConversationMessages,
    searchTerm,
    filters,
    advancedFilterState,
    conversations,
    conversationsTimeoutMessage,
    totalInDatabase,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    refetch,
    containerStyle,
    setSearchTerm,
    setFilters,
    setAdvancedFilterState,
    applyChatFilterState,
    clearAdvancedFilterState,
    setShowConversationList,
    setShowMessageHistory,
    setShowConversationDetails,
    setSelectedConversation,
    setMessages: setMessagesMap,
    applyMessagesIfActive,
    handleSelectConversation,
    handleEndConversation,
    conversationDetails,
    detailsAccessDenied,
    detailsDenied,
    refetchConversationDetails,
    sendMessageMutation,
    isError,
    conversationsError,
    createConversation,
    isCreatingConversation: isCreating,
    errorCreatingConversation: errorCreating,
    aiInsights,
    isLoadingInsights,
    aiInsightsError,
    loadAiInsights,
    hasMoreMessages: messagesPageState.hasMore,
    isLoadingMoreMessages: messagesPageState.isLoadingMore,
    loadOlderMessages,
    handleDeleteMessage,
    handleReactMessage,
    handleEditWhatsappMessage,
    handleForwardWhatsappMessage,
    handleEditNote,
    handleDeleteNote,
    deletePersonalConversation,
  };
}
